import { createContext, type ReactNode, useContext, useState } from 'react';

import { useEffectOnce } from '@genially/ui/build/hooks/useEffectOnce';
import { usePlayPlayersLimit } from '@genially/ui/build/hooks/usePlayPlayersLimit';

import { PlaySpinner } from '../../../../../../shared/application/components/PlaySpinner/PlaySpinner';
import { config } from '../../../../config/config';

export type PlayersLimitsContextType = Awaited<
  ReturnType<ReturnType<typeof usePlayPlayersLimit>['getLimitInfo']>
>;

const PlayersLimitsProviderContext = createContext({});

export interface PlayersLimitsProps {
  teamId: string;
  children: ReactNode;
}

const PlayersLimitsProvider = ({ teamId, children }: PlayersLimitsProps) => {
  const { loading, getLimitInfo } = usePlayPlayersLimit(config.urls.p2pServer);

  const [data, setData] = useState<Awaited<ReturnType<typeof getLimitInfo>>>();

  useEffectOnce(() => {
    getLimitInfo(teamId).then(limits => setData(limits));
  });

  if (loading || !data) {
    return <PlaySpinner />;
  }

  const context: PlayersLimitsContextType = data;

  return (
    <PlayersLimitsProviderContext.Provider value={context}>
      {children}
    </PlayersLimitsProviderContext.Provider>
  );
};

const usePlayersLimits = () => {
  const context = useContext(PlayersLimitsProviderContext) as PlayersLimitsContextType;

  if (Object.keys(context).length === 0) {
    throw new Error('usePlayersLimits must be used within a PlayersLimitsProvider');
  }

  return context;
};

export { PlayersLimitsProvider, usePlayersLimits };
